import { useEffect, useState } from 'react';
import {
  GeoProjection,
  geoAlbersUsa,
  geoConicConformal,
  geoConicEqualArea,
} from 'd3-geo';

import echarts from '../echarts';
import { getAreaRegionIds } from './regions';

type Area = NonNullable<Studio.MapOptions['region']>;

const AREAS: Area[] = ['usCanada', 'us', 'canada'];

// What each area's outlines are registered with ECharts as
export const getMapName = (area: Area) => `studio-${area}`;

// Drawn as each is usually drawn: the US as Albers (Alaska and Hawaii set
// in beside the lower 48), Canada as Statistics Canada's Lambert, and both
// together on one equal-area cone about the continent's middle
const PROJECTIONS: Record<Area, () => GeoProjection> = {
  us: () => geoAlbersUsa(),
  canada: () => geoConicConformal().parallels([49, 77]).rotate([96, 0]),
  usCanada: () => geoConicEqualArea().parallels([30, 60]).rotate([96, 0]),
};

// A projection as ECharts takes one: it fits what's projected into the
// chart's room itself
export const toEChartsProjection = (area: Area) => {
  const projection = PROJECTIONS[area]();
  return {
    project: (point: number[]) =>
      projection(point as [number, number]) ?? [NaN, NaN],
    unproject: (point: number[]) =>
      (projection.invert && projection.invert(point as [number, number])) ?? [
        NaN,
        NaN,
      ],
  };
};

type MapSource = Parameters<typeof echarts.registerMap>[1];
type Outlines = typeof import('./usCanada.json');

// An area's outlines, as ECharts takes them (the JSON's own types have
// plain strings where GeoJSON has its geometry types)
const toMapSource = ({
  outlines,
  area,
}: {
  outlines: Outlines;
  area: Area;
}): MapSource => {
  const ids = new Set(getAreaRegionIds(area));
  return {
    type: 'FeatureCollection',
    features: outlines.features
      .filter((feature) => ids.has(feature.properties.name))
      .map((feature) => ({
        type: 'Feature',
        properties: feature.properties,
        geometry:
          feature.geometry.type === 'MultiPolygon'
            ? {
                type: 'MultiPolygon',
                coordinates: feature.geometry.coordinates as number[][][][],
              }
            : {
                type: 'Polygon',
                coordinates: feature.geometry.coordinates as number[][][],
              },
      })),
  };
};

let loading: Promise<void> | undefined;
let isLoaded = false;

// The outlines load in their own chunk, the first time a Map draws, and are
// registered once for every area
export const loadRegionMaps = (): Promise<void> => {
  if (!loading) {
    loading = import('./usCanada.json').then((module) => {
      const outlines: Outlines = module.default ?? module;
      AREAS.forEach((area) => {
        echarts.registerMap(getMapName(area), toMapSource({ outlines, area }));
      });
      isLoaded = true;
    });
  }
  return loading;
};

// Whether the Map outlines are ready to draw with, loading them if asked
// for (and not yet loaded)
export const useRegionMaps = (isNeeded: boolean): boolean => {
  const [isReady, setIsReady] = useState<boolean>(isLoaded);
  useEffect(() => {
    if (!isNeeded || isReady) {
      return undefined;
    }
    let isActive = true;
    loadRegionMaps().then(() => {
      if (isActive) {
        setIsReady(true);
      }
    });
    return () => {
      isActive = false;
    };
  }, [isNeeded, isReady]);
  return isReady;
};
